/**
 * Sentry noise filter.
 *
 * Drops events that originate from third-party SDK retry/timeout logs that
 * surface as console.error or unhandled errors but are not actionable bugs
 * in app code. The OneSignal SDK in particular emits "Op failed, pausing"
 * and "Fetch timeout" lines whenever its push-subscription lifecycle hits a
 * transient network or auth condition; these accounted for hundreds of
 * Sentry events per week with no corresponding user-visible failure.
 *
 * A second category is an expected outcome that a dependency happens to log at
 * error level. A rejected sign-in is the app working correctly — the screen
 * shows a toast and reports the attempt through analytics — but Amplify logs
 * it and the console integration promotes it to an error event.
 *
 * The other category is script injected into the page by an embedding host
 * rather than by us. Microsoft Office and Outlook open links through an
 * in-app browser that injects its own bridge code, and that code rejects with
 * an "Object Not Found Matching Id" string when its host object has already
 * gone away. It arrives as a stringly unhandled rejection with no stacktrace
 * and no app frame, so there is nothing to fix and nothing to route.
 *
 * The Rive runtime logs a bare "Problem loading file" line whenever one of the
 * decorative `.riv` animations fails to fetch or decode from Rive's own CDN.
 * The log carries no stacktrace, no URL, and no app frame, and the runtime
 * emits it before anything we could hook, so the adapter cannot suppress it at
 * the source. A failed decorative animation renders nothing and blocks no
 * flow, so the log is not worth an error-level event.
 *
 * The next category is a transient socket drop that Twilio's twilsock layer
 * logs itself. `hooks/useTwilio.ts` deliberately records these as breadcrumbs
 * rather than events, because the SDK reconnects on its own and the user sees
 * nothing — but twilsock also writes its own `console.error`, and the console
 * integration promotes that straight past our policy into an error event. It
 * rejects each in-flight request with a `TwilsockError` as the socket closes,
 * which is where the bare "disconnected" and status-0 lines come from.
 * Matching them here restores the intended breadcrumb-only treatment. The
 * actionable terminus stays `connectionState === 'denied'`, which `useTwilio`
 * handles and reports once recovery is exhausted.
 *
 * The last category is a message a dependency synthesizes about its own
 * emitter rather than about anything that happened. When a twilsock token
 * update fails on a network error, twilsock reports it by emitting `'error'`
 * on its internal channel — an emitter nothing subscribes, two levels below
 * the client object we hold. An unlistened `'error'` is the one case Node's
 * emitter contract turns into a throw, and this one is thrown from an async
 * function, so it lands as an unhandled rejection naming no cause and no frame
 * of ours. Subscribing that emitter would mean reaching through two layers of
 * private SDK state to suppress a rejection with no user-visible effect, so
 * the report is dropped here instead. The token update itself is already
 * retried by the SDK's own expiry events.
 *
 * Every rule below is anchored to a message shape observed in production, and
 * most are additionally scoped to the origin that produces them. Message text
 * alone cannot say who threw something, so a word as ordinary as
 * "disconnected" would otherwise take an identically worded app failure with
 * it. The origin is the closest thing to provenance available at this point:
 * stack frames here still point at the bundle, since symbolication happens
 * after upload, so the module that raised an error is not yet knowable.
 *
 * Scope a rule whenever its wording could plausibly come from somewhere else,
 * and leave it unscoped only when the message could not have been written by
 * anything but the dependency it names. Getting a scope wrong lets noise back
 * in, which is visible; getting a pattern too broad hides a real failure,
 * which is not. Prefer the first mistake.
 *
 * Do not add a rule that could match an app failure at the same origin: a
 * generic "Timeout" or "Load failed" line would take real API errors with it.
 * The page-unload `AbortError` was rejected on those grounds — WebKit emits
 * that exact string for any fetch cancelled by teardown or navigation,
 * including our own requests aborted by a route change, and both arrive at the
 * same origin, so no scope here separates them.
 */

type SentryEventLike = {
  message?: string | null;
  logger?: string | null;
  exception?: {
    values?:
      | ({ value?: string | null; mechanism?: { type?: string | null } | undefined } | undefined)[]
      | undefined;
  };
};

/**
 * Where an event came from, as far as the browser can tell before upload.
 *
 * `app` covers anything we captured deliberately and, on native, anything the
 * platform's global error handler reported — the two are indistinguishable
 * there, since only web installs a console integration.
 */
type EventOrigin = 'console' | 'unhandledRejection' | 'app';

type NoiseRule = {
  pattern: RegExp;
  /**
   * Origins the pattern may drop an event from. Omit when the message is
   * distinctive enough that no other source could produce it.
   */
  origins?: EventOrigin[];
};

const NOISE_RULES: NoiseRule[] = [
  // OneSignal SDK retry/lifecycle logs (most volume)
  { pattern: /^Op failed, pausing:/, origins: ['console'] },
  { pattern: /^Op failed \(no retry\):/, origins: ['console'] },
  { pattern: /^Fetch timeout https:\/\/api\.onesignal\.com/, origins: ['console'] },
  { pattern: /^SetAlias failed:/, origins: ['console'] },
  { pattern: /^OneSignal service worker not found!$/, origins: ['console'] },

  // "Unrecognized operation" is a phrase any library could log, so this one
  // leans on the origin rather than on the wording.
  { pattern: /^Unrecognized operation: /, origins: ['console'] },

  // A person mistyped their password. Amplify logs the rejected sign-in, and
  // the console integration turns that into an error event; the screens that
  // trigger it already show a toast and report the attempt through
  // `trackLoginFailed`, which is where auth alerting reads from. Matched on
  // the message rather than on NotAuthorizedException, because that same
  // exception also carries revoked-token and disabled-account cases worth
  // keeping.
  //
  // Deliberately unscoped. On web Amplify logs it and the console integration
  // promotes it; on native there is no console integration, so the same
  // rejected sign-in arrives through the platform's global error handler and
  // is indistinguishable from a deliberate capture. Both are the same expected
  // outcome, so an origin restriction would only let the native half back in.
  { pattern: /Incorrect username or password/ },

  // Microsoft Office / Outlook in-app browser bridge, not our code
  {
    pattern: /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
    origins: ['unhandledRejection'],
  },

  // Rive CDN fetch/decode failure for a decorative animation
  { pattern: /^Problem loading file; may be corrupt!$/, origins: ['console'] },

  // react-native-web's BackHandler stub, which logs this on every
  // `addEventListener` call and then returns a no-op subscription. Gluestack's
  // `useKeyboardDismissable` (via `@gluestack-ui/react-native-aria`) registers a
  // hardware-back listener whenever a modal, sheet or popover is enabled, and
  // that hook has no web branch. Android's back button does not exist on web,
  // so the dropped listener loses nothing. Fully anchored: the stub emits
  // exactly this string.
  {
    pattern: /^BackHandler is not supported on web and should not be used\.$/,
    origins: ['console'],
  },

  // Twilio socket-close noise. Fully anchored, because the SDK's own lines are
  // exactly these with no surrounding prose — so a real failure that merely
  // mentions "disconnected" is still reported. The trailing pair is Sync's
  // status and code, which default to zero when nothing answered; a nonzero
  // pair means the service replied and is deliberately left reportable.
  { pattern: /^disconnected$/, origins: ['console'] },
  { pattern: /^disconnected \(status: 0, code: 0\)$/, origins: ['console'] },

  // twilsock's unlistened 'error' emit on a failed token update. The `(false)`
  // is twilsock's own first argument to `emit('error', false, …)`, stringified
  // by the emitter into the message — an unusual enough argument to keep this
  // off another library's version of the same synthesized line.
  { pattern: /^Uncaught, unspecified "error" event\. \(false\)$/, origins: ['unhandledRejection'] },

  // A Sync map the SDK still holds locally but the service no longer has,
  // rejected inside the SDK with nothing of ours on the stack. The status and
  // code are Sync's own, which keeps this off an app failure worded the same.
  { pattern: /^Map not found \(status: 404, code: 54200\)$/, origins: ['unhandledRejection'] },
];

/**
 * Classify where an event came from.
 *
 * Only the console integration and the unhandled-rejection handler stamp a
 * mechanism we can read here. The rejection handler's type carries a prefix on
 * web (`auto.browser.global_handlers.`) and none on native, so it is matched by
 * its suffix, or every rule scoped to a rejection stops matching on web. Anything else is treated as ours. That is the
 * conservative direction: an unrecognized origin is reported, never dropped.
 */
function getEventOrigin(event: SentryEventLike): EventOrigin {
  const mechanism = event.exception?.values?.[0]?.mechanism?.type;

  if (mechanism?.endsWith('onunhandledrejection')) {
    return 'unhandledRejection';
  }

  if (mechanism === 'console' || event.logger === 'console') {
    return 'console';
  }

  return 'app';
}

function getEventMessageText(event: SentryEventLike): string | undefined {
  if (typeof event.message === 'string' && event.message.length > 0) {
    return event.message;
  }

  const firstException = event.exception?.values?.[0];

  if (firstException && typeof firstException.value === 'string') {
    return firstException.value;
  }

  return undefined;
}

export function isSentryNoiseEvent(event: SentryEventLike): boolean {
  const text = getEventMessageText(event);

  if (!text) {
    return false;
  }

  const origin = getEventOrigin(event);

  return NOISE_RULES.some(
    (rule) => rule.pattern.test(text) && (!rule.origins || rule.origins.includes(origin))
  );
}
